import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API Client Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
        'Content-Type': 'application/json',
    },
});

const clearStoredAuth = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
};

// Request interceptor for adding auth tokens
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Add auth token if exists
        const token = localStorage.getItem('authToken');
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Log requests in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.data);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => {
        // Log responses in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API] Response:`, response.data);
        }
        return response;
    },
    (error: AxiosError) => {
        // Handle specific error cases
        if (error.response) {
            const { status, data } = error.response;

            // Handle authentication errors
            if (status === 401) {
                clearStoredAuth();

                const requestUrl = error.config?.url || '';
                const isAuthRequest = requestUrl.includes('/api/auth/login') ||
                    requestUrl.includes('/api/auth/bootstrap-admin');

                if (!isAuthRequest && window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
            }

            // Handle forbidden
            if (status === 403) {
                console.error('Access forbidden');
            }

            // Handle not found
            if (status === 404) {
                console.error('Resource not found');
            }

            // Handle server errors
            if (status >= 500) {
                console.error('Server error occurred');
            }

            // Log error in development
            if (process.env.NODE_ENV === 'development') {
                console.error(`[API] Error ${status}:`, data);
            }
        } else if (error.request) {
            // Request made but no response
            console.error('Network error - no response received');
        } else {
            // Error setting up request
            console.error('Request setup error:', error.message);
        }

        return Promise.reject(error);
    }
);

// Helper function to handle API errors
export const handleApiError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ detail?: string; error?: string }>;

        if (axiosError.response?.data) {
            return axiosError.response.data.detail ||
                axiosError.response.data.error ||
                'An error occurred';
        }

        if (axiosError.message) {
            return axiosError.message;
        }
    }

    return 'An unexpected error occurred';
};

export default apiClient;
